/**
 * API Service - Centralized API client for the application.
 *
 * This module provides:
 * - Axios instance with interceptors for auth and error handling
 * - Authentication endpoints (login, signup, etc.)
 * - Items CRUD endpoints (example module)
 * - Generic HTTP methods for extending the API
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  LoginRequest,
  SignUpRequest,
  RequestCodeRequest,
  ValidateAccountRequest,
  User,
  AuthTokens,
  // PM_GITHUB_START
  GitHubAuthUrlResponse,
  GitHubCallbackRequest,
  GitHubCallbackResponse,
  GitHubStatusResponse
  // PM_GITHUB_END
} from '../types/auth';
import {
  Item,
  ItemListItem,
  CreateItemRequest,
  UpdateItemRequest,
} from '../types/items';
import backendManager from './BackendManager';
import env from '../config/environment';
import { canReplayAfterWake, isSleepingGatewayStatus } from './apiRetryPolicy';
import {
  CreateStudySession,
  Questionnaire,
  ResearchConfig,
  ResearchVideo,
  ResearchSummary,
  StudyConfig,
  StudySession,
} from '../types/study';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: env.apiBaseUrl,
      withCredentials: true, // Important for cookie-based authentication
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to ensure backend is ready and use dynamic URL
    this.api.interceptors.request.use(
      async (config) => {
        // Ensure backend is running (only affects on-demand backends)
        await backendManager.ensureBackendReady();

        // Update base URL dynamically (in case backend IP changed)
        config.baseURL = backendManager.getApiBaseUrl();

        // Add auth token if available
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle token refresh and server errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) return Promise.reject(error);

        const canReplay = canReplayAfterWake(originalRequest.method);

        // Handle network errors (backend might be cold-starting)
        if (!error.response && backendManager.isOnDemandEnabled()) {
          // A mutation may already have committed before its connection failed.
          // Mark readiness stale, but only replay safe requests invisibly.
          backendManager.reset();
          if (canReplay && !originalRequest._backendRetry) {
            originalRequest._backendRetry = true;
            await backendManager.ensureBackendReady();
            return this.api(originalRequest);
          }
        }

        const gatewayMayBeSleeping = isSleepingGatewayStatus(error.response?.status);
        if (gatewayMayBeSleeping && backendManager.isOnDemandEnabled()) {
          backendManager.reset();
          if (canReplay && !originalRequest._backendRetry) {
            originalRequest._backendRetry = true;
            await backendManager.ensureBackendReady();
            return this.api(originalRequest);
          }
        }

        // Never replay a mutation after a 5xx: it may have committed before a
        // gateway timed out. The next explicit user action wakes first.
        if (error.response?.status >= 500) return Promise.reject(error);

        // Handle 401 Unauthorized - try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await this.refreshToken();
              const { access } = response.data;
              localStorage.setItem('access_token', access);
              originalRequest.headers.Authorization = `Bearer ${access}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // ==================================================================
  // GENERIC HTTP METHODS
  // ==================================================================

  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.get(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config);
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config);
  }

  // ==================================================================
  // AUTHENTICATION ENDPOINTS
  // ==================================================================

  async login(data: LoginRequest): Promise<AxiosResponse<AuthTokens>> {
    return this.api.post('/auth/login/', data);
  }

  async signUp(data: SignUpRequest): Promise<AxiosResponse<{ user: User } & AuthTokens>> {
    return this.api.post('/auth/sign-up/', data);
  }

  async requestCode(data: RequestCodeRequest): Promise<AxiosResponse<{ provider: string }>> {
    return this.api.post('/auth/request-code/', data);
  }

  async validateAccount(data: ValidateAccountRequest): Promise<AxiosResponse<AuthTokens>> {
    return this.api.post('/auth/validate-account/', data);
  }

  async requestValidateToken(data: RequestCodeRequest): Promise<AxiosResponse<{ provider: string }>> {
    return this.api.post('/auth/request-validate-token/', data);
  }

  async refreshToken(): Promise<AxiosResponse<AuthTokens>> {
    return this.api.post('/auth/token-refresh/');
  }

  async signOut(): Promise<AxiosResponse<void>> {
    return this.api.post('/auth/sign-out/');
  }

  // ==================================================================
  // USER ENDPOINTS
  // ==================================================================

  async getCurrentUser(): Promise<AxiosResponse<User>> {
    return this.api.get('/users/me/');
  }

  // ==================================================================
  // GITHUB OAUTH ENDPOINTS
  // ==================================================================
  // PM_GITHUB_START
  async getGitHubAuthUrl(): Promise<AxiosResponse<GitHubAuthUrlResponse>> {
    return this.api.get('/github/auth-url/');
  }

  async gitHubCallback(data: GitHubCallbackRequest): Promise<AxiosResponse<GitHubCallbackResponse>> {
    return this.api.post('/github/callback/', data);
  }

  async disconnectGitHub(): Promise<AxiosResponse<{ success: boolean; message: string }>> {
    return this.api.post('/github/disconnect/');
  }

  async getGitHubStatus(): Promise<AxiosResponse<GitHubStatusResponse>> {
    return this.api.get('/github/status/');
  }
  // PM_GITHUB_END

  // ==================================================================
  // ITEMS ENDPOINTS (Example CRUD module)
  // ==================================================================

  async getItems(): Promise<AxiosResponse<ItemListItem[]>> {
    return this.api.get('/items/');
  }

  async getItem(id: number): Promise<AxiosResponse<Item>> {
    return this.api.get(`/items/${id}/`);
  }

  async createItem(data: CreateItemRequest): Promise<AxiosResponse<Item>> {
    return this.api.post('/items/', data);
  }

  async updateItem(id: number, data: UpdateItemRequest): Promise<AxiosResponse<Item>> {
    return this.api.patch(`/items/${id}/`, data);
  }

  async deleteItem(id: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/items/${id}/`);
  }

  async archiveItem(id: number): Promise<AxiosResponse<Item>> {
    return this.api.post(`/items/${id}/archive/`);
  }

  async activateItem(id: number): Promise<AxiosResponse<Item>> {
    return this.api.post(`/items/${id}/activate/`);
  }

  // ==================================================================
  // LOFTUS–PALMER STUDY
  // ==================================================================

  async getStudyConfig(): Promise<AxiosResponse<StudyConfig>> {
    return this.api.get('/study/config/');
  }

  async createStudySession(data: CreateStudySession): Promise<AxiosResponse<StudySession>> {
    return this.api.post('/study/sessions/', data);
  }

  async getStudySession(token: string): Promise<AxiosResponse<StudySession>> {
    return this.api.get(`/study/sessions/${encodeURIComponent(token)}/`);
  }

  async startPlayback(token: string): Promise<AxiosResponse<{ status: string; started_at: string; required_duration_seconds: number }>> {
    return this.api.post(`/study/sessions/${encodeURIComponent(token)}/playback/start/`, { position_seconds: 0 });
  }

  async completePlayback(token: string, endedPosition: number, duration: number): Promise<AxiosResponse<{ status: string; completed_at: string; questionnaire: Questionnaire }>> {
    return this.api.post(`/study/sessions/${encodeURIComponent(token)}/playback/complete/`, {
      ended_position_seconds: endedPosition,
      duration_seconds: duration,
    });
  }

  async submitStudyResponses(token: string, responses: Array<{ question_id: number; value: string | number | boolean | string[] }>): Promise<AxiosResponse<{ status: string; participant_code: string; completed_at: string; duration_seconds: number; message: string }>> {
    return this.api.post(`/study/sessions/${encodeURIComponent(token)}/responses/`, { responses });
  }

  async withdrawStudySession(token: string): Promise<AxiosResponse<{ status: 'withdrawn'; participant_code: string; withdrawn_at: string; duration_seconds: number; message: string }>> {
    return this.api.post(`/study/sessions/${encodeURIComponent(token)}/withdraw/`, {});
  }

  async getResearchConfig(config?: AxiosRequestConfig): Promise<AxiosResponse<ResearchConfig>> {
    return this.api.get('/research/config/', config);
  }

  async updateResearchConfig(data: Partial<{ active: boolean; title: string; description: string; consent_title: string; consent_body: string; consent_withdrawal_text: string; video_url: string; video_duration_seconds: number; video_rights_confirmed: boolean; video: { url: string; duration_seconds: number; duration_verified: boolean; rights_confirmed: boolean }; speed_unit: string }>): Promise<AxiosResponse<ResearchConfig>> {
    return this.api.patch('/research/config/', data);
  }

  async saveResearchCondition(data: Record<string, unknown>, id?: number) {
    return id ? this.api.patch(`/research/conditions/${id}/`, data) : this.api.post('/research/conditions/', data);
  }

  async saveResearchQuestion(data: Record<string, unknown>, id?: number) {
    return id ? this.api.patch(`/research/questions/${id}/`, data) : this.api.post('/research/questions/', data);
  }

  async uploadResearchVideo(data: FormData): Promise<AxiosResponse<ResearchConfig>> {
    return this.api.post('/research/video/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }

  async saveResearchVideo(data: FormData | Record<string, unknown>, id?: number): Promise<AxiosResponse<ResearchVideo>> {
    const url = id ? `/research/videos/${id}/` : '/research/videos/';
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined;
    return id ? this.api.patch(url, data, config) : this.api.post(url, data, config);
  }

  async deleteResearchVideo(id: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/research/videos/${id}/`);
  }

  async getResearchSessions(search = '', status = '', page = 1) {
    return this.api.get('/research/sessions/', { params: { search, status, page } });
  }

  async getResearchSession(id: number) { return this.api.get(`/research/sessions/${id}/`); }

  async reviewResearchSession(id: number, data: { review_status: string; review_note: string }) {
    return this.api.patch(`/research/sessions/${id}/`, data);
  }

  async getResearchSummary(config?: AxiosRequestConfig): Promise<AxiosResponse<ResearchSummary>> {
    return this.api.get('/research/summary/', config);
  }

  async downloadResearchExport(format: 'csv' | 'xlsx', table?: 'response_frequencies'): Promise<AxiosResponse<Blob>> {
    return this.api.get(`/research/export.${format}`, { responseType: 'blob', params: table ? { table } : undefined });
  }
}

const apiService = new ApiService();
export default apiService;
export const api = apiService;
